import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Table, FormControl, Button, Modal, Form } from 'react-bootstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import cx from 'classnames';
import moment from 'moment';

import s from './RefundsList.css';
import messages from '../../../locale/messages';
import debounce from '../../../helpers/debounce';
import CustomPagination from '../../CustomPagination';
import { effectiveDefaultLabel, BEARER_LABELS, formatIDR } from './refundAllocationLabel';
import {
    approveRefundRequest,
    rejectRefundRequest,
    retryRefundRequest,
    resolveRefundRequest,
    createRefundRequest,
} from '../../../actions/siteadmin/RefundRequests/refundRequestActions';

// The refund engine's state vocabulary (asap-super-api features/refunds).
const REFUND_STATUSES = ['requested', 'approved', 'processing', 'succeeded', 'failed', 'rejected', 'cancelled'];

const REASON_CODES = [
    'ITEM_MISSING', 'ITEM_WRONG', 'ITEM_QUALITY', 'ORDER_NEVER_ARRIVED',
    'LATE_DELIVERY', 'OVERCHARGE', 'PRICING_ERROR', 'DUPLICATE_CHARGE',
    'CANCELLED_AFTER_CAPTURE', 'FRAUD', 'GOODWILL',
];

const STATUS_COLORS = {
    requested: { background: '#fff3cd', color: '#856404' },
    approved: { background: '#d1ecf1', color: '#0c5460' },
    processing: { background: '#d1ecf1', color: '#0c5460' },
    succeeded: { background: '#d4edda', color: '#155724' },
    failed: { background: '#f8d7da', color: '#721c24' },
    rejected: { background: '#e2e3e5', color: '#383d41' },
    cancelled: { background: '#e2e3e5', color: '#383d41' },
};

export class RefundsList extends Component {

    static propTypes = {
        refundRequests: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            currentPage: 1,
            refundStatus: '',
            searchList: '',
            // One modal at a time: { kind: 'approve'|'reject'|'resolve'|'create', row }
            modal: null,
            amount: '',
            notes: '',
            outcome: 'failed',
            providerRefundId: '',
            referenceType: 'booking',
            referenceId: '',
            reasonCode: 'GOODWILL',
            bearer: '',              // '' = engine default for the reason
            requestKey: '',
            busy: false,
        };
        this.paginationData = this.paginationData.bind(this);
        this.handleStatusFilter = this.handleStatusFilter.bind(this);
        this.handleKeywordSearch = debounce(this.handleKeywordSearch.bind(this), 250);
        this.closeModal = this.closeModal.bind(this);
        this.submitModal = this.submitModal.bind(this);
    }

    refetchList(overrides = {}) {
        const { refundRequests: { refetch } } = this.props;
        const { currentPage, refundStatus, searchList } = this.state;
        refetch({
            currentPage,
            refundStatus: refundStatus || null,
            searchList: searchList || null,
            ...overrides,
        });
    }

    paginationData(currentPage) {
        this.setState({ currentPage });
        this.refetchList({ currentPage });
    }

    handleStatusFilter(refundStatus) {
        this.setState({ refundStatus, currentPage: 1 });
        this.refetchList({ refundStatus: refundStatus || null, currentPage: 1 });
    }

    handleKeywordSearch(searchList) {
        this.setState({ searchList, currentPage: 1 });
        this.refetchList({ searchList: searchList || null, currentPage: 1 });
    }

    openModal(kind, row) {
        this.setState({
            modal: { kind, row },
            amount: row && row.amount != null ? String(Math.round(row.amount)) : '',
            notes: '',
            outcome: 'failed',
            providerRefundId: '',
            referenceType: 'booking',
            referenceId: '',
            reasonCode: 'GOODWILL',
            bearer: '',
            // One key per opened form: a double-click on submit replays the same
            // request and the engine returns the existing row.
            requestKey: `siteadmin-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        });
    }

    closeModal() {
        this.setState({ modal: null, busy: false });
    }

    async submitModal(approveNow) {
        const { modal, amount, notes, outcome, providerRefundId, referenceType, referenceId, reasonCode, bearer, requestKey } = this.state;
        const {
            approveRefundRequest, rejectRefundRequest,
            resolveRefundRequest, createRefundRequest,
        } = this.props;
        if (!modal || this.state.busy) return;
        this.setState({ busy: true });

        let result;
        if (modal.kind === 'approve') {
            result = await approveRefundRequest(modal.row.id, amount, bearer || null);
        } else if (modal.kind === 'reject') {
            result = await rejectRefundRequest(modal.row.id, notes);
        } else if (modal.kind === 'resolve') {
            result = await resolveRefundRequest(modal.row.id, outcome, providerRefundId);
        } else if (modal.kind === 'create') {
            result = await createRefundRequest({
                referenceType, referenceId, requestKey,
                amount, reasonCode, reasonNotes: notes,
                approveNow: Boolean(approveNow),
                bearer: bearer || null,
            });
        }

        this.setState({ busy: false });
        if (result && result.status === 200) {
            this.closeModal();
            this.refetchList();
        }
    }

    async handleRetry(row) {
        const { retryRefundRequest } = this.props;
        const result = await retryRefundRequest(row.id);
        if (result && result.status === 200) this.refetchList();
    }

    renderStatusBadge(row) {
        const style = STATUS_COLORS[row.refundStatus] || {};
        return (
            <div>
                <span className={s.statusBadge} style={style}>{row.refundStatus}</span>
                {row.failureCode && <div className={s.failureNote}>{row.failureCode}{row.failureMessage ? `: ${row.failureMessage}` : ''}</div>}
            </div>
        );
    }

    renderActions(row) {
        const status = row.refundStatus;
        return (
            <div>
                {status === 'requested' && <Button size="sm" variant="success" className={s.actionBtn} onClick={() => this.openModal('approve', row)}>
                    <FormattedMessage {...messages.approve} />
                </Button>}
                {status === 'requested' && <Button size="sm" variant="outline-danger" className={s.actionBtn} onClick={() => this.openModal('reject', row)}>
                    <FormattedMessage {...messages.decline} />
                </Button>}
                {status === 'failed' && <Button size="sm" variant="warning" className={s.actionBtn} onClick={() => this.handleRetry(row)}>
                    <FormattedMessage {...messages.refundRetry} />
                </Button>}
                {status === 'processing' && <Button size="sm" variant="outline-secondary" className={s.actionBtn} onClick={() => this.openModal('resolve', row)}>
                    <FormattedMessage {...messages.refundResolve} />
                </Button>}
            </div>
        );
    }

    // Who bears the cost. Empty = what a plain approval will ACTUALLY apply:
    // the request's saved proposal when one exists, else the reason default
    // (shown explicitly so an approval never charges someone by surprise).
    renderBearerPicker(reasonCode, disabled, row) {
        const { bearer } = this.state;
        return (
            <Form.Group>
                <Form.Label>Borne by</Form.Label>
                {disabled && <div className={s.metaNote}>
                    Enter an amount to choose the payer now — without one, attribution is decided at approval.
                </div>}
                <FormControl as="select" value={bearer} disabled={disabled}
                    onChange={(event) => this.setState({ bearer: event.target.value })}>
                    <option value="">{effectiveDefaultLabel(row, reasonCode)}</option>
                    <option value="shop">{BEARER_LABELS.shop}</option>
                    <option value="driver">{BEARER_LABELS.driver}</option>
                    <option value="platform">{BEARER_LABELS.platform}</option>
                </FormControl>
            </Form.Group>
        );
    }

    renderModal() {
        const { modal, amount, notes, outcome, providerRefundId, referenceType, referenceId, reasonCode, busy } = this.state;
        if (!modal) return null;
        const { kind, row } = modal;

        const titles = {
            approve: messages.refundApproveTitle,
            reject: messages.refundRejectTitle,
            resolve: messages.refundResolve,
            create: messages.refundNewTitle,
        };

        return (
            <Modal show onHide={this.closeModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title><FormattedMessage {...titles[kind]} /></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {row && <p className={s.metaNote}>
                        #{row.id} · {row.referenceType} #{row.referenceId} · {row.reasonCode}
                        {row.reasonNotes ? ` — ${row.reasonNotes}` : ''}
                    </p>}

                    {kind === 'approve' && <div>
                        <Form.Group>
                            <Form.Label><FormattedMessage {...messages.refundAmountIDR} /></Form.Label>
                            <FormControl type="number" min="1" step="1" value={amount}
                                onChange={(event) => this.setState({ amount: event.target.value })} />
                        </Form.Group>
                        {this.renderBearerPicker(row ? row.reasonCode : reasonCode, false, row)}
                    </div>}

                    {(kind === 'reject' || kind === 'create') && <Form.Group>
                        <Form.Label><FormattedMessage {...messages.refundReasonNotes} /></Form.Label>
                        <FormControl as="textarea" rows={3} value={notes}
                            onChange={(event) => this.setState({ notes: event.target.value })} />
                    </Form.Group>}

                    {kind === 'resolve' && <div>
                        <p className={s.metaNote}>
                            Check the Xendit dashboard first. "failed" = no refund exists there
                            (re-enables retry); "succeeded" = it completed but the webhook was lost.
                        </p>
                        <Form.Group>
                            <Form.Label><FormattedMessage {...messages.status} /></Form.Label>
                            <FormControl as="select" value={outcome}
                                onChange={(event) => this.setState({ outcome: event.target.value })}>
                                <option value="failed">failed (not at Xendit)</option>
                                <option value="succeeded">succeeded (webhook lost)</option>
                            </FormControl>
                        </Form.Group>
                        {outcome === 'succeeded' && <Form.Group>
                            <Form.Label>Xendit refund id</Form.Label>
                            <FormControl value={providerRefundId}
                                onChange={(event) => this.setState({ providerRefundId: event.target.value })} />
                        </Form.Group>}
                    </div>}

                    {kind === 'create' && <div>
                        <Form.Group>
                            <Form.Label><FormattedMessage {...messages.bookingId} /></Form.Label>
                            <div className={s.filterRow}>
                                <FormControl as="select" className={s.filterControl} value={referenceType}
                                    onChange={(event) => this.setState({ referenceType: event.target.value })}>
                                    <option value="booking">booking</option>
                                    <option value="ride_booking">ride_booking</option>
                                </FormControl>
                                <FormControl type="number" className={s.filterControl} value={referenceId}
                                    onChange={(event) => this.setState({ referenceId: event.target.value })} />
                            </div>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label><FormattedMessage {...messages.reason} /></Form.Label>
                            <FormControl as="select" value={reasonCode}
                                onChange={(event) => this.setState({ reasonCode: event.target.value })}>
                                {REASON_CODES.map((code) => <option key={code} value={code}>{code}</option>)}
                            </FormControl>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label><FormattedMessage {...messages.refundAmountIDR} /></Form.Label>
                            <FormControl type="number" min="1" step="1" value={amount}
                                onChange={(event) => this.setState({ amount: event.target.value })} />
                        </Form.Group>
                        {this.renderBearerPicker(reasonCode, !amount)}
                    </div>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.closeModal} disabled={busy}>
                        <FormattedMessage {...messages.cancel} />
                    </Button>
                    {kind === 'create' && <Button variant="outline-primary"
                        onClick={() => this.submitModal(false)}
                        disabled={busy || !referenceId}>
                        Create for review
                    </Button>}
                    {kind === 'create' && <Button variant="primary"
                        onClick={() => this.submitModal(true)}
                        disabled={busy || !referenceId || !amount}>
                        Create &amp; refund now
                    </Button>}
                    {kind !== 'create' && <Button variant="primary" onClick={() => this.submitModal(false)}
                        disabled={busy
                            || (kind === 'approve' && !amount)
                            || (kind === 'resolve' && outcome === 'succeeded' && !providerRefundId)}>
                        <FormattedMessage {...messages.submitButton} />
                    </Button>}
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        const { refundRequests } = this.props;
        const { currentPage, refundStatus } = this.state;
        const list = refundRequests && refundRequests.getRefundRequests;
        const results = (list && list.results) || [];
        const count = (list && list.count) || 0;

        return (
            <div>
                <div className={s.filterRow}>
                    <FormControl as="select" className={s.filterControl} value={refundStatus}
                        onChange={(event) => this.handleStatusFilter(event.target.value)}>
                        <option value="">All statuses</option>
                        {REFUND_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
                    </FormControl>
                    <FormControl className={s.filterControl}
                        placeholder="userId / charge id"
                        onChange={(event) => this.handleKeywordSearch(event.target.value)} />
                    <div className={s.newRefundBtnWrap}>
                        <Button variant="primary" onClick={() => this.openModal('create', null)}>
                            <FormattedMessage {...messages.refundNewTitle} />
                        </Button>
                    </div>
                </div>

                {list && list.status && list.status !== 200 && <p className={s.failureNote}>{list.errorMessage}</p>}

                <div className={s.tableSection}>
                    <Table responsive size="sm">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th><FormattedMessage {...messages.bookingId} /></th>
                                <th><FormattedMessage {...messages.amount} /></th>
                                <th><FormattedMessage {...messages.reason} /></th>
                                <th><FormattedMessage {...messages.status} /></th>
                                <th><FormattedMessage {...messages.date} /></th>
                                <th><FormattedMessage {...messages.action} /></th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((row) => (
                                <tr key={row.id}>
                                    <td>{row.id}</td>
                                    <td>
                                        {row.referenceType === 'ride_booking' ? 'ride' : 'order'} #{row.referenceId}
                                        <div className={s.metaNote}>{row.requestedBy}</div>
                                    </td>
                                    <td>
                                        {formatIDR(row.amount)}
                                        {row.allocations && row.allocations.length > 0 && <div className={s.metaNote}>
                                            {row.allocations.map((leg) => `${leg.bearer} ${formatIDR(leg.amount)}`).join(' · ')}
                                        </div>}
                                    </td>
                                    <td>
                                        {row.reasonCode}
                                        {row.reasonNotes && <div className={s.metaNote}>{row.reasonNotes}</div>}
                                    </td>
                                    <td>{this.renderStatusBadge(row)}</td>
                                    <td>{row.createdAt ? moment(row.createdAt).format('DD MMM YYYY HH:mm') : '—'}</td>
                                    <td>{this.renderActions(row)}</td>
                                </tr>
                            ))}
                            {results.length === 0 && <tr>
                                <td colSpan={7} className="text-center"><FormattedMessage {...messages.noResult} /></td>
                            </tr>}
                        </tbody>
                    </Table>
                </div>

                {count > 0 && <CustomPagination
                    total={count}
                    currentPage={currentPage}
                    defaultCurrent={1}
                    defaultPageSize={20}
                    change={this.paginationData}
                    paginationLabel={'refunds'}
                />}

                {this.renderModal()}
            </div>
        );
    }
}

const mapDispatch = {
    approveRefundRequest,
    rejectRefundRequest,
    retryRefundRequest,
    resolveRefundRequest,
    createRefundRequest,
};

export default injectIntl(withStyles(s)(connect(null, mapDispatch)(RefundsList)));
